import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { ConnectPortalHeader } from "./ConnectPortalHeader";
import { ConnectPortalNav } from "./ConnectPortalNav";
import { ConnectPortalBottomNav } from "./ConnectPortalBottomNav";
import { ConnectBackHeader, getRouteInfo } from "./ConnectBackHeader";
import { getPreviousPortalPath } from "@/hooks/usePortalNavHistory";
import { RadioMiniPlayer } from "@/components/connect/RadioMiniPlayer";
import { useRadio } from "@/contexts/RadioContext";
import { OfflineBanner } from "@/components/connect/OfflineBanner";
import { useAuth } from "@/contexts/AuthContext";
import { useIsDesktop } from "@/hooks/useIsTablet";
import { useIsNative } from "@/hooks/useIsNative";
import { AuthedWebShell } from "./AuthedWebShell";
import { Logo } from "@/components/ui/Logo";
import elefaAppIcon from "@/assets/elefa-app-icon.webp";
import { NativePageTransition } from "@/components/native/NativePageTransition";
import { useEdgeSwipeBack } from "@/hooks/useEdgeSwipeBack";
import { useScrollRestoration } from "@/hooks/useScrollRestoration";
import { NativeLargeTitle } from "./NativeLargeTitle";
import useNativeStatusBar from "@/hooks/useNativeStatusBar";
import { useNotificationBanner } from "@/hooks/useNotificationBanner";
import { usePortalNavHistory } from "@/hooks/usePortalNavHistory";

// Prefetch every tab's chunk + the heavy shared widgets so tab switches
// feel instant. Runs on idle shortly after first paint - never competes
// with the launch frame, but ready long before the user hits Contacts.
const prefetchCorePages = () => {
  import("@/pages/connect-portal/ConnectDashboard");
  import("@/pages/connect-portal/ConnectDialer");
  import("@/pages/connect-portal/ConnectRadioPage");
  import("@/pages/connect-portal/ConnectMessagesPage");
  import("@/pages/connect-portal/ConnectMePage");
  import("@/pages/connect-portal/ConnectContactsPage");
  import("@/pages/connect-portal/ConnectHistoryPage").catch(() => {});
  import("@/pages/connect-portal/ConnectTopUpPage").catch(() => {});
  import("@/pages/connect-portal/ConnectNotificationsPage").catch(() => {});
  // Heavy shared widget pulled in by Contacts/Dial - warm it explicitly so
  // first tap on Contacts on Android doesn't pay parse cost.
  import("@/components/connect/NativeContactsTab").catch(() => {});
};


interface ConnectPortalLayoutProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  hideChrome?: boolean;
  statusBarTheme?: "light" | "dark" | "call" | "transparent";
}

export function ConnectPortalLayout({ children, requireAuth = true, hideChrome = false, statusBarTheme = "light" }: ConnectPortalLayoutProps) {
  const { currentStation } = useRadio();

  const [authFuseExpired, setAuthFuseExpired] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const isNative = useIsNative();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const mainScrollerRef = useRef<HTMLElement>(null);
  const { user, isLoading: authLoading } = useAuth();

  // Phase 2 - iOS edge-swipe-back + per-route scroll memory
  useEdgeSwipeBack(mainScrollerRef, !hideChrome);
  useScrollRestoration(mainScrollerRef);

  // P3 - Foreground in-app notification banner (sonner toast on realtime INSERT).
  useNotificationBanner();

  // Track in-portal navigation so back buttons can return to actual previous page.
  usePortalNavHistory();

  // Keep normal app pages below the native status bar so time/carrier/battery remain visible.
  useNativeStatusBar(statusBarTheme);

  // Warm non-current tabs in the background. Never compete with launch.
  useEffect(() => {
    let idleId: number | null = null;
    const timer = window.setTimeout(() => {
      if (document.visibilityState !== "visible") return;
      if ("requestIdleCallback" in window) {
        idleId = (window as any).requestIdleCallback(prefetchCorePages, { timeout: 15_000 });
      } else {
        prefetchCorePages();
      }
    }, 1_500);

    return () => {
      window.clearTimeout(timer);
      if (idleId !== null && "cancelIdleCallback" in window) {
        (window as any).cancelIdleCallback(idleId);
      }
    };
  }, []);

  useEffect(() => {
    if (!requireAuth || !authLoading) {
      setAuthFuseExpired(false);
      return;
    }

    const timer = window.setTimeout(() => setAuthFuseExpired(true), 5_000);
    return () => window.clearTimeout(timer);
  }, [authLoading, requireAuth]);

  useEffect(() => {
    if (!requireAuth) return;
    if (authLoading && !authFuseExpired) return;

    if (!user) {
      const redirect = encodeURIComponent(location.pathname + location.search);
      navigate(`/connect-portal/auth?redirect=${redirect}`, { replace: true });
      return;
    }

  }, [authFuseExpired, authLoading, location.pathname, location.search, navigate, requireAuth, user]);

  // Web (non-native) authed users get the Rebtel-style wallet-first shell.
  // Native iOS/Android continue with the original native-app chrome below.
  if (!isNative && !hideChrome && user && requireAuth) {
    return (
      <div className="bg-background">
        <OfflineBanner />
        <AuthedWebShell>{children}</AuthedWebShell>
      </div>
    );
  }

  return (
    <div className={cn("bg-background", hideChrome || !isDesktop ? "h-[100dvh] overflow-hidden flex flex-col" : "min-h-[100dvh]")}>
      <OfflineBanner />
      {/* Desktop Layout with Sidebar */}
      {isDesktop ? (
        <div className="flex min-h-[100dvh]">
          {/* Sidebar */}
          <aside className={`fixed left-0 top-0 h-screen w-64 bg-sidebar-background border-r border-sidebar-border z-40 ${hideChrome ? 'hidden' : ''}`}>
            <div className="flex flex-col h-full">
              {/* Logo - icon only */}
              <div className="h-16 flex items-center justify-center border-b border-sidebar-border overflow-hidden">
                <img
                  src={elefaAppIcon}
                  alt="elefa Connect"
                  className="h-9 w-9 rounded-[22%] object-cover shrink-0"
                />
              </div>
              {/* Navigation */}
              <ConnectPortalNav />
            </div>
          </aside>
          
          <main className={`flex-1 ${hideChrome ? '' : 'ml-64'}`}>
            {!hideChrome && <ConnectPortalHeader />}
            {!hideChrome && <ConnectBackHeader sentinelRef={sentinelRef} />}
            {!hideChrome && <div ref={sentinelRef} className="h-px" />}
            <div
              className={cn(hideChrome ? '' : 'py-6', 'relative')}
              style={
                hideChrome
                  ? undefined
                  : { paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${currentStation ? '7rem' : '3.5rem'})` }
              }
            >
              {!hideChrome && <NativeLargeTitle />}
              <NativePageTransition>{children}</NativePageTransition>
            </div>
            {/* Desktop persistent mini player */}
            {!hideChrome && (
              <div className="fixed bottom-0 left-0 right-0 z-50 ml-64">
                <RadioMiniPlayer />
              </div>
            )}
          </main>
        </div>
      ) : (
        /* Mobile Layout - native app structure */
        <div className={cn("flex flex-col flex-1 min-h-0", hideChrome ? "overflow-hidden" : "overflow-hidden")}>
          {!hideChrome && <ConnectPortalHeader />}
          {!hideChrome && <ConnectBackHeader sentinelRef={sentinelRef} />}
          {!hideChrome && <div ref={sentinelRef} className="h-px" />}
          <main
            ref={mainScrollerRef}
            className={cn(
              "flex-1 overflow-x-hidden relative native-scroll [overscroll-behavior:contain]",
              hideChrome ? 'relative overflow-hidden flex flex-col' : 'overflow-y-auto'
            )}
            style={
              hideChrome
                ? undefined
                : {
                    // Bottom-nav clearance.
                    // Nav is 52px tall + 6px top padding + safe-area, and on
                    // Android `env(safe-area-inset-bottom)` resolves to 0 (the
                    // system gesture/3-button bar is rendered OUTSIDE the
                    // webview via setDecorFitsSystemWindows=true). The fixed
                    // app nav can still occlude ~80-90px of trailing content
                    // (icon row + label + shadow), so the scroller needs a
                    // generous bottom inset so the final line of any page is
                    // always fully visible without scrolling under the tabs.
                    // (Fix: Android Referrals/Help text cut off, 2026-06-23.)
                    paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${currentStation ? '16rem' : '12rem'})`,
                  }



            }
          >
            {!hideChrome && <NativeLargeTitle />}
            <NativePageTransition>{children}</NativePageTransition>
          </main>
          {!hideChrome && (
            <>
              <div
                className="pointer-events-none fixed left-0 right-0 z-40 px-2"
                style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 3.25rem)" }}
              >
                <RadioMiniPlayer />
              </div>
              <ConnectPortalBottomNav />
            </>
          )}
        </div>
      )}
    </div>
  );
}
